import { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";

const MAX_MB = 6;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function BannerEditor() {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/filmmaker/handle").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Cover banner must be JPG, PNG or WebP.");
    if (file.size > MAX_MB * 1024 * 1024) return toast.error(`Cover banner exceeds ${MAX_MB} MB.`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("banner", file);
      const { data } = await api.post("/filmmaker/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMeta((m) => ({ ...m, ...data }));
      toast.success("Cover banner updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/filmmaker/banner");
      setMeta((m) => ({ ...m, has_banner: false, banner_updated_at: null }));
      toast.success("Cover banner removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove banner");
    } finally {
      setBusy(false);
    }
  };

  if (!meta) return null;
  const src = meta.has_banner ? `${API}/users/${meta.id}/banner?v=${encodeURIComponent(meta.banner_updated_at || "")}` : null;

  return (
    <div className="mb-6 bg-surface border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="banner-panel">
      <Label>Cover banner</Label>
      <p className="text-xs text-foreground/55 mt-1 mb-3">Optional wide image across the top of your profile · JPG, PNG or WebP · max {MAX_MB} MB · about 1600×500 px.</p>
      <div className="relative aspect-[16/5] rounded-xl overflow-hidden border border-white/10 bg-gradient-to-br from-terracotta/30 via-ember/10 to-gold/10 mb-4" data-testid="banner-preview">
        {src ? (
          <img src={src} alt="Cover banner" className="w-full h-full object-cover" data-testid="banner-preview-img" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-foreground/45 text-sm">No banner — your profile uses the warm gradient</div>
        )}
        {busy && <div className="absolute inset-0 bg-ink/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>}
      </div>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="banner-file-input" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="bg-terracotta hover:bg-ember text-white h-10" data-testid="banner-upload-btn">
          <ImagePlus className="w-4 h-4" />{meta.has_banner ? "Change banner" : "Upload banner"}
        </Button>
        {meta.has_banner && (
          <Button type="button" onClick={remove} disabled={busy} variant="outline" className="border-white/15 hover:bg-white/5 h-10" data-testid="banner-remove-btn">
            <Trash2 className="w-4 h-4" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
